import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit, Signal } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AffectedItemsFormGroup, CodCreateFormGroup } from '../../../models/cod-create.model';
import { OrderLineTableComponent } from '../../../../shared/components/order-line-table/order-line-table.component';
import { IOrderLine, NatureOfComplaintDomestic } from '@cod-monorepo/interfaces';
import { NatureOfComplaintInternational } from '@cod-monorepo/interfaces';
import { domesticMapping, internationalMapping } from './affected-items.config';
import { Region } from '@cod-monorepo/interfaces';
import { ButtonModule, InputModule, SelectModule } from '@sag-ui';
import { CodState } from '../../../state/cod.state';
import { select } from '@ngxs/store';

@Component({
    selector: 'app-cod-affected-items-step',
    templateUrl: './affected-items.component.html',
    imports: [
        CommonModule,
        SelectModule,
        InputModule,
        FormsModule,
        ReactiveFormsModule,
        ButtonModule,
        OrderLineTableComponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CodAffectedItemsStepComponent implements OnInit, OnChanges {
    @Input({ required: true })
    affectedItemsForm: FormGroup<AffectedItemsFormGroup>;
    @Input({ required: true }) formGroup: FormGroup<CodCreateFormGroup>;
    @Input() complaint: NatureOfComplaintDomestic | NatureOfComplaintInternational | null = null;
    @Input() readonly = false;

    public isDomestic: Signal<boolean> = select(CodState.isDomestic);

    public selectLabel = '';
    public selectPlaceholder = '';
    public items: string[] = [];
    public form: FormGroup;
    public formControlName = '';
    public hideOrderLineTableFor: string[] = [];
    public hideOrderLineTable = false;

    constructor(private changeDetectorRef: ChangeDetectorRef) {}

    ngOnInit(): void {
        this.initSelectOptions();
    }

    ngOnChanges(): void {
        this.initSelectOptions();
    }

    public get confirmed(): boolean {
        return this.affectedItemsForm.controls.confirmed.value ?? false;
    }

    public get lineItems(): IOrderLine[] {
        return this.affectedItemsForm.controls.affectedOrderLines.value ?? [];
    }

    public get valid(): boolean {
        const orderLinesSelected = (this.affectedItemsForm.controls.affectedOrderLines.value ?? []).length > 0;
        const typeSelected = this.affectedItemsForm.controls.typeValid.value ?? false;
        return this.hideOrderLineTable || (orderLinesSelected && typeSelected);
    }

    public get disabled(): boolean {
        return this.affectedItemsForm.disabled;
    }

    public setConfirmed(): void {
        this.affectedItemsForm.controls.confirmed.setValue(true);
    }

    public handleSelectionChange(value: IOrderLine[]): void {
        this.affectedItemsForm.controls.confirmed.setValue(false);
        this.affectedItemsForm.get('affectedOrderLines')?.setValue(value);
    }

    public handleSelectChange(value: Event): void {
        this.form.get(this.formControlName)?.setValue(value);

        this.affectedItemsForm.controls.typeValid.setValue(!!value);

        if (this.hideOrderLineTable) {
            this.setConfirmed();
        }
    }

    private initSelectOptions(): void {
        let config;

        if (this.domestic) {
            config = domesticMapping[this.natureOfComplaint as NatureOfComplaintDomestic];
        } else {
            config = internationalMapping[this.natureOfComplaint as NatureOfComplaintInternational];
        }

        if (!config) {
            return;
        }

        this.selectLabel = config.selectLabel;
        this.selectPlaceholder = config.selectPlaceholder;
        this.items = this.readonly
            ? config.items
            : config.items.filter(item => !config.hiddenInCreation?.includes(item));
        this.formControlName = config.formControlName;
        this.form = this.formGroup.get(config.form) as FormGroup;

        if (!this.form?.get(this.formControlName)) {
            console.error(`FormControl '${this.formControlName}' is missing in the current FormGroup`);
        }

        this.changeDetectorRef.detectChanges();
    }

    private get domestic(): boolean {
        return this.formGroup.get('region')?.get('region')?.value === Region.NAM;
    }

    private get natureOfComplaint(): NatureOfComplaintDomestic | NatureOfComplaintInternational {
        if (this.domestic) {
            return this.formGroup.get('referenceNumberDomestic')?.get('natureOfComplaint')
                ?.value as NatureOfComplaintDomestic;
        } else {
            return this.formGroup.get('referenceNumberInternational')?.get('natureOfComplaint')
                ?.value as NatureOfComplaintInternational;
        }
    }
}
